const RECOVERY_WINDOW_MILLISECONDS = 10_000
const RECOVERY_TIMESTAMP_STORAGE_KEY = 'joyful.vite-preload-error-recovery'

export interface VitePreloadErrorRecoveryOptions {
  getPendingNavigationPath: () => string | null
}

export function isStaleClientBundleError(error: Error) {
  const message = String(error)
  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('error loading dynamically imported module') ||
    message.includes('Importing a module script failed')
  )
}

export function shouldSuppressStaleClientBundleError(error: Error) {
  if (!isStaleClientBundleError(error)) {
    return false
  }

  const recoveryTimestamp = Number(
    window.sessionStorage.getItem(RECOVERY_TIMESTAMP_STORAGE_KEY),
  )
  // A timestamp from the previous document means this page already reloaded once.
  const recoveredOnEarlierPage = recoveryTimestamp < performance.timeOrigin
  const hasRecentlyRecovered =
    Date.now() - recoveryTimestamp < RECOVERY_WINDOW_MILLISECONDS
  return !(recoveredOnEarlierPage && hasRecentlyRecovered)
}

export function registerVitePreloadErrorRecovery({
  getPendingNavigationPath,
}: VitePreloadErrorRecoveryOptions) {
  const handlePreloadError = () => {
    if (hasRecentlyRecoveredFromStaleClientBundle()) {
      return
    }

    const currentTimestamp = Date.now()
    window.sessionStorage.setItem(
      RECOVERY_TIMESTAMP_STORAGE_KEY,
      String(currentTimestamp),
    )

    const pendingNavigationPath = getPendingNavigationPath()
    if (pendingNavigationPath) {
      window.location.assign(pendingNavigationPath)
      return
    }

    window.location.reload()
  }

  window.addEventListener('vite:preloadError', handlePreloadError)

  return () => {
    window.removeEventListener('vite:preloadError', handlePreloadError)
  }
}

function hasRecentlyRecoveredFromStaleClientBundle() {
  const recoveryTimestamp = Number(
    window.sessionStorage.getItem(RECOVERY_TIMESTAMP_STORAGE_KEY),
  )
  return Date.now() - recoveryTimestamp < RECOVERY_WINDOW_MILLISECONDS
}
